import React, { useRef } from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import {
  Button,
  CircularProgress,
  TextField,
  Tooltip,
  MenuItem,
  Select
} from '@material-ui/core'
import copy from 'copy-to-clipboard'
import { ImagePicker } from '~views/shared'
import { Save, Delete } from '@material-ui/icons'
import { showSnackbarError, showSnackbarSuccess } from 'views/utils/snackbar'
import { useStyles } from './SectorForm.style'
import { CirclePicker } from 'react-color'
import { validateExtension } from 'views/utils/functions'
import { PositionDescriptor } from 'config/constants'

const hexColorRegex = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/

const validationSchema = Yup.object().shape({
  SectorName: Yup.string()
    .required('Es requerido ingresar un nombre')
    .max(250, 'La cantidad máxima es de 250 caracteres'),
  Column: Yup.number()
    .required('Es requerido ingresar la cantidad de columnas')
    .integer('Solo se permiten valores enteros')
    .typeError('Solo se permiten valores numéricos')
    .min(1, 'La cantidad no puede ser menor a 1')
    .max(90, 'La cantidad máxima es de 90'),
  Row: Yup.number()
    .required('Es requerido ingresar la cantidad de filas')
    .integer('Solo se permiten valores enteros')
    .typeError('Solo se permiten valores numéricos')
    .min(1, 'La cantidad no puede ser menor a 1')
    .max(90, 'La cantidad máxima es de 90'),
  Color: Yup.string()
    .nullable()
    .matches(hexColorRegex, 'No es un color HEX válido')
})

export const SectorForm = ({
  values,
  actionText,
  handleSubmit,
  hasSold,
  onCancel,
  actionPending
}) => {
  const classes = useStyles()
  const fileInput = useRef(null)

  const copyToClipBoard = (urlImage) => {
    try {
      copy(urlImage)
      showSnackbarSuccess('¡Imagen copiada al portapapeles!')
    } catch (error) {
      showSnackbarError(error)
    }
  }

  const normalizeHexColor = (value) => {
    if (!value) return '#000000'
    return value.startsWith('#') ? value : `#${value}`
  }

  const handleSelectImage = () => {
    fileInput.current.click()
  }

  const customColors = [
    // Rojos / Rosas / Magentas
    '#f44336', // rojo
    '#ff6e42', // rojo cálido / naranja rojizo
    '#e91e63', // rosa
    '#FF0080', // magenta brillante

    // Morados / Indigo
    '#9c27b0',
    '#673ab7',
    '#3f51b5',

    // Azules / Cianes
    '#1d7bc9',
    '#03a9f4',
    '#00e5ff', // cian eléctrico (más brillante)
    '#00bcd4', // cyan (más oscuro)

    // Puente azul-gris
    '#607d8b', // blue-grey

    // Teal / Verdes
    '#009688', // teal
    '#4caf50', // verde
    '#8bc34a', // verde claro / lima

    // Amarillos / Ámbar / Dorado
    '#ffeb3b', // amarillo
    '#ffc107', // ámbar
    '#b8860b', // dorado oscuro

    // Naranjas
    '#ff9800',

    // Marrones / Neutros
    '#8B4513', // saddle brown
    '#795548' // marrón grisáceo
  ]

  return (
    <Formik
      enableReinitialize
      initialValues={values}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}>
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldError,
        setFieldTouched,
        isSubmitting
      }) => (
        <form
          noValidate
          onSubmit={handleSubmit}
          className={classes.formContainer}>
          <div className={classes.fieldsContainer}>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.SectorName &&
                    touched.SectorName &&
                    errors.SectorName) ||
                  'Nombre del Sector'
                }
                name='SectorName'
                value={values.SectorName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.SectorName && Boolean(errors.SectorName)}
                fullWidth
                disabled={actionPending}
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Column && touched.Column && errors.Column) ||
                  'Cantidad de columnas'
                }
                name='Column'
                type='number'
                value={values.Column}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Column && Boolean(errors.Column)}
                fullWidth
                disabled={actionPending || hasSold}
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
              <div className={classes.fieldRow}>
                <Select
                  name='ColumnDescriptor'
                  value={values.ColumnDescriptor}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={actionPending}
                  variant='outlined'
                  size='small'
                  className={classes.fieldSelect}
                  fullWidth>
                  <MenuItem value={PositionDescriptor.Numeric}>
                    Numérico
                  </MenuItem>
                  <MenuItem value={PositionDescriptor.Alphabetic}>
                    Alfabético
                  </MenuItem>
                  <MenuItem value={PositionDescriptor.Roman}>Romano</MenuItem>
                </Select>
              </div>
            </div>
            <div className={classes.fieldRow}>
              <TextField
                label={
                  (errors.Row && touched.Row && errors.Row) ||
                  'Cantidad de filas'
                }
                name='Row'
                type='number'
                value={values.Row}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Row && Boolean(errors.Row)}
                fullWidth
                disabled={actionPending || hasSold}
                required
                size='small'
                variant='outlined'
                className={classes.field}
              />
              <div className={classes.fieldRow}>
                <Select
                  name='RowDescriptor'
                  value={values.RowDescriptor}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={actionPending}
                  variant='outlined'
                  size='small'
                  className={classes.fieldSelect}
                  fullWidth>
                  <MenuItem value={PositionDescriptor.Numeric}>
                    Numérico
                  </MenuItem>
                  <MenuItem value={PositionDescriptor.Alphabetic}>
                    Alfabético
                  </MenuItem>
                  <MenuItem value={PositionDescriptor.Roman}>Romano</MenuItem>
                </Select>
              </div>
            </div>
            <div className={classes.containerColor}>
              <TextField
                label={
                  (errors.Color && touched.Color && errors.Color) || 'Color'
                }
                name='Color'
                value={values.Color}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.Color && Boolean(errors.Color)}
                inputProps={{ maxLength: 10 }}
                disabled={actionPending}
                size='small'
                variant='outlined'
                className={classes.colorInput}
              />
              <input
                type='color'
                value={normalizeHexColor(values.Color)}
                onChange={(e) => {
                  const colorValue = e.target.value
                  setFieldValue('Color', colorValue)
                }}
                disabled={actionPending}
                className={classes.colorField}
              />
              <CirclePicker
                colors={customColors}
                color={normalizeHexColor(values.Color)}
                circleSize={24}
                circleSpacing={12}
                onChangeComplete={(color) => {
                  setFieldValue('Color', color.hex)
                }}
                onSwatchHover={(color, e) => {}}
              />
            </div>
            <div style={{ marginLeft: 16 }}>
              <input
                type='file'
                id='ImageFile'
                style={{ display: 'none' }}
                accept='.png,.jpg,.jpeg,.pjp,.pjpeg,.jfif'
                onChange={(event) => {
                  if (event.target.files.length > 0) {
                    const file = event.target.files[0]
                    if (validateExtension(file.name)) {
                      setFieldValue('ImageFile', file)
                      setFieldError('ImageFile', '')
                    }
                  }
                }}
                ref={fileInput}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                <Button
                  onClick={handleSelectImage}
                  color='primary'
                  variant='contained'
                  disabled={actionPending || isSubmitting}
                  style={{
                    marginRight: 10,
                    textTransform: 'none',
                    marginBlock: 16
                  }}>
                  Seleccionar Imagen
                </Button>
                {!!values.ImageFile && (
                  <>
                    <Tooltip
                      title={values.ImageFile.name ?? 'Imagen'}
                      placement='right'>
                      <div>
                        <ImagePicker
                          className={classes.imagePicker}
                          onImageChange={() => {}}
                          onClickImage={copyToClipBoard}
                          url={
                            values.ImageFile instanceof File
                              ? URL.createObjectURL(values.ImageFile)
                              : values.ImageFile || ''
                          }
                        />
                      </div>
                    </Tooltip>
                    <Button
                      onClick={() => {
                        setFieldValue('ImageId', '')
                        setFieldValue('ImageFile', null)
                        if (fileInput.current) {
                          fileInput.current.value = null
                        }
                      }}
                      color='primary'
                      variant='contained'
                      startIcon={<Delete />}
                      style={{ marginLeft: 10, textTransform: 'none' }}
                      disabled={actionPending || isSubmitting}>
                      Quitar Imagen
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={classes.actionsContainer}>
            <Button
              onClick={onCancel}
              disabled={actionPending || isSubmitting}
              autoFocus={false}
              color='primary'>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                values.SectorName = values.SectorName.trim()
                values.Color = values.Color.trim()
                Object.keys(values).forEach((key) => {
                  setFieldTouched(key, true)
                })
                handleSubmit()
              }}
              disabled={actionPending || isSubmitting}
              color='primary'
              variant='contained'
              startIcon={<Save />}
              autoFocus={false}>
              {actionPending || isSubmitting ? (
                <CircularProgress size={24} color='secondary' />
              ) : (
                actionText
              )}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default SectorForm
